import React, {useCallback, useEffect, useState} from 'react';
import {App, Empty, Modal, Space, Spin, Tag, Tooltip, Typography} from 'antd';
import {ArrowLeftOutlined, BranchesOutlined, ExportOutlined, MessageOutlined, ToolOutlined} from '@ant-design/icons';
import {botService, PromptHistoryResponse} from '../../services/botService';
import chatService from '../../services/chatService';
import {ChatMessage} from '../../types/chat';
import PromptItem from './PromptItem';
import ChatMessageComponent from '../../components/chat/ChatMessage';
import MarkdownRenderer from '../../components/common/MarkdownRenderer';

const {Text} = Typography;

/**
 * 视图模式
 */
type ViewMode = 'chain' | 'session';

/**
 * 单个item的展开状态接口
 * 从PromptItem复制,保持一致
 */
interface ItemExpandedState {
    promptContent: boolean; // Prompt内容是否展开
    aiResponse: boolean;   // AI模型响应是否展开
}

interface ConversationChainModalProps {
    visible: boolean;
    onClose: () => void;
    recordId: number | null;
    apiKeyId?: number | null;
    onTransfer?: (sessionId: number, userId: string) => void; // 转移到AI助手的回调
    publicView?: boolean; // 公开展示模式，禁用会话消息等需要登录的功能
}

/**
 * 会话链路弹窗组件
 * 展示完整的会话链路(从最顶层父级到当前记录)
 * 支持切换到会话消息视图
 */
const ConversationChainModal: React.FC<ConversationChainModalProps> = ({
                                                                           visible,
                                                                           onClose,
                                                                           recordId,
                                                                           apiKeyId,
                                                                           onTransfer,
                                                                           publicView
                                                                       }) => {
    const {message: messageApi} = App.useApp();
    const [chainData, setChainData] = useState<PromptHistoryResponse[]>([]);
    const [loading, setLoading] = useState(false);

    // 视图模式：chain(会话链路) | session(会话消息)
    const [viewMode, setViewMode] = useState<ViewMode>('chain');
    const [currentSessionId, setCurrentSessionId] = useState<number | null>(null);
    const [currentUserId, setCurrentUserId] = useState<string>('default');
    const [sessionMessages, setSessionMessages] = useState<ChatMessage[]>([]);
    const [sessionLoading, setSessionLoading] = useState(false);

    // 预过滤掉内部函数调用和产出校验消息，确保tool消息连续可分组
    const displayMessages = React.useMemo(() => {
        return sessionMessages.filter(msg => {
            const internalFunction = msg.functionName === 'output_validation'
                || msg.functionName === 'output_repair_feedback'
                || msg.functionName === 'submit_final_output';
            const internalContent = msg.content?.startsWith('[function_call]')
                || msg.content?.startsWith('[starting_gun]')
                || msg.content?.startsWith('[output_validation]')
                || msg.content?.startsWith('[output_validation_aborted]');
            return !(internalFunction || internalContent);
        });
    }, [sessionMessages]);

    // 管理每个会话记录的展开状态
    const [expandedItems, setExpandedItems] = useState<Record<string, ItemExpandedState>>({});
    // 工具调用结果组的展开状态（存储组的起始索引）
    const [expandedToolGroup, setExpandedToolGroup] = useState<Set<number>>(new Set());

    /**
     * 计算从指定索引开始的连续tool消息数量
     */
    const getToolGroupCount = useCallback((messages: ChatMessage[], index: number): number => {
        if (messages[index]?.role !== 'tool') return 0;
        if (index > 0 && messages[index - 1]?.role === 'tool') return 0;
        let count = 0;
        let idx = index;
        while (idx < messages.length && messages[idx]?.role === 'tool') {
            count++;
            idx++;
        }
        return count;
    }, []);

    /**
     * 获取从指定索引开始的所有连续tool消息
     */
    const getToolGroupMessages = useCallback((messages: ChatMessage[], index: number): ChatMessage[] => {
        const group: ChatMessage[] = [];
        let idx = index;
        while (idx < messages.length && messages[idx]?.role === 'tool') {
            group.push(messages[idx]);
            idx++;
        }
        return group;
    }, []);

    /**
     * 切换工具调用结果组的展开/收起状态
     */
    const toggleToolGroup = useCallback((index: number) => {
        setExpandedToolGroup(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    }, []);

    /**
     * 更新指定item的展开状态
     * @param itemId 项目ID
     * @param key 状态key
     * @param value 是否展开
     */
    const updateItemExpandedState = (itemId: string, key: keyof ItemExpandedState, value: boolean) => {
        setExpandedItems(prev => {
            const currentItemState = prev[itemId] || {
                promptContent: false,
                aiResponse: false
            };

            return {
                ...prev,
                [itemId]: {
                    ...currentItemState,
                    [key]: value
                }
            };
        });
    };

    /**
     * 确保指定item的展开状态对象完整
     */
    const getCompleteExpandedState = (itemId: string): ItemExpandedState => {
        const existingState = expandedItems[itemId];

        if (existingState &&
            typeof existingState.promptContent === 'boolean' &&
            typeof existingState.aiResponse === 'boolean') {
            return existingState;
        }

        return {
            promptContent: existingState?.promptContent || false,
            aiResponse: existingState?.aiResponse || false
        };
    };

    /**
     * 加载会话链路数据
     */
    const loadConversationChain = async () => {
        if (!recordId) {
            return;
        }

        try {
            setLoading(true);
            const response = await botService.getConversationChain(recordId, apiKeyId ?? undefined);

            if (response.success && response.data) {
                setChainData(response.data);
            } else {
                messageApi.error(response.message || '获取会话链路失败');
                setChainData([]);
            }
        } catch (error) {
            // console.error('获取会话链路失败:', error);
            messageApi.error('获取会话链路失败');
            setChainData([]);
        } finally {
            setLoading(false);
        }
    };

    /**
     * 切换到会话消息视图
     * @param sessionId 会话ID
     */
    const switchToSessionView = async (sessionId: number) => {
        setCurrentSessionId(sessionId);
        setViewMode('session');
        loadSessionMessages(sessionId);
    };

    /**
     * 切换回会话链路视图
     */
    const switchToChainView = () => {
        setViewMode('chain');
        setCurrentSessionId(null);
        setSessionMessages([]);
    };

    /**
     * 加载会话消息
     */
    const loadSessionMessages = async (sessionId: number) => {
        try {
            setSessionLoading(true);
            const messages = await chatService.getSessionMessages(sessionId);
            setSessionMessages(messages);

            // 从消息中提取userId（如果消息中有userId字段）
            if (messages.length > 0 && messages[0].userId) {
                setCurrentUserId(messages[0].userId);
            } else {
                setCurrentUserId('default');
            }
        } catch (error) {
            // console.error('获取会话消息失败:', error);
            messageApi.error('获取会话消息失败');
            setSessionMessages([]);
            setCurrentUserId('default');
        } finally {
            setSessionLoading(false);
        }
    };

    /**
     * 处理转移到AI助手
     */
    const handleTransferToChat = () => {
        if (currentSessionId && onTransfer) {
            onTransfer(currentSessionId, currentUserId);
            onClose(); // 关闭弹窗
        } else {
            messageApi.warning('没有可转移的会话');
        }
    };

    // 当弹窗打开时加载数据
    useEffect(() => {
        if (visible && recordId) {
            loadConversationChain();
        }
    }, [visible, recordId]);

    // 当弹窗关闭时清空数据
    useEffect(() => {
        if (!visible) {
            setChainData([]);
            setExpandedItems({});
            setViewMode('chain');
            setCurrentSessionId(null);
            setSessionMessages([]);
        }
    }, [visible]);

    return (
        <Modal
            title={
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    paddingRight: '40px'  // 为关闭图标留出空间
                }}>
                    {viewMode === 'chain' ? (
                        <Space>
                            <BranchesOutlined style={{color: '#9254de'}}/>
                            <span>会话链路</span>
                            {chainData.length > 0 && (
                                <Text style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)'}}>
                                    {chainData.length} 条记录
                                </Text>
                            )}
                        </Space>
                    ) : (
                        <Space>
                            <Tooltip title="返回会话链路">
                                <span
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: 'var(--pt-primary)',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    onClick={switchToChainView}
                                >
                                    <ArrowLeftOutlined/>
                                </span>
                            </Tooltip>
                            <MessageOutlined style={{color: 'var(--pt-bull)'}}/>
                            <span>会话消息</span>
                            {displayMessages.length > 0 && (
                                <Text style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)'}}>
                                    {displayMessages.length} 条消息
                                </Text>
                            )}
                        </Space>
                    )}

                    <Space>
                        {/* 会话图标按钮 - 仅在链路模式下显示，公开展示时禁用 */}
                        {viewMode === 'chain' && chainData.length > 0 && (
                            <Tooltip title={publicView ? '登录后查看会话消息' : '查看会话消息'}>
                                <span
                                    style={{
                                        cursor: publicView ? 'not-allowed' : 'pointer',
                                        fontSize: '16px',
                                        color: publicView ? 'rgba(255, 255, 255, 0.25)' : '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    onClick={publicView ? undefined : () => {
                                        const targetItem = chainData.find(item => item.decisionId === String(recordId));
                                        const sessionId = targetItem?.chatSessionId || chainData[0]?.chatSessionId;
                                        if (sessionId) {
                                            switchToSessionView(sessionId);
                                        } else {
                                            messageApi.warning('该记录没有关联的会话消息');
                                        }
                                    }}
                                >
                                    <MessageOutlined/>
                                </span>
                            </Tooltip>
                        )}

                        {/* 转移按钮 - 仅在会话消息模式下显示 */}
                        {viewMode === 'session' && currentSessionId && (
                            <Tooltip title="转移到AI助手">
                                <span
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        color: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                    onClick={handleTransferToChat}
                                >
                                    <ExportOutlined/>
                                </span>
                            </Tooltip>
                        )}
                    </Space>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width="90%"
            footer={null}
            style={{
                backgroundColor: 'transparent', // 移除Modal外层的背景色，由content控制
                paddingBottom: 0
            }}
            styles={{
                content: {
                    backgroundColor: 'var(--pt-bg-panel)',
                    borderRadius: '16px', // 增加圆角
                    border: '1px solid rgba(255,255,255,0.07)', // 增加边框
                    padding: 0,
                    overflow: 'hidden' // 确保圆角生效
                },
                header: {
                    backgroundColor: 'var(--pt-bg-panel)',
                    padding: '16px 24px',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                    marginBottom: 0
                },
                body: {
                    backgroundColor: 'var(--pt-bg-panel)',
                    maxHeight: '75vh',
                    overflowY: 'auto',
                    padding: '24px' // 增加内边距
                }
            }}
        >
            {viewMode === 'chain' ? (
                // 会话链路视图
                loading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px'
                    }}>
                        <Spin size="large">
                            <div style={{marginTop: 8, color: '#999'}}>加载中...</div>
                        </Spin>
                    </div>
                ) : chainData.length === 0 ? (
                    <Empty
                        description="暂无会话链路数据"
                        style={{marginTop: '60px'}}
                    />
                ) : (
                    <div>
                        {chainData.map((item, index) => (
                            <div key={item.decisionId} style={{marginBottom: '16px'}}>
                                {/* 序号标识 */}
                                <div style={{
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <Tag
                                        color={index === chainData.length - 1 ? 'blue' : 'default'}
                                        style={{margin: 0}}
                                    >
                                        #{index + 1}
                                    </Tag>
                                    {index === chainData.length - 1 && (
                                        <Text style={{fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)'}}>
                                            (当前记录)
                                        </Text>
                                    )}
                                </div>

                                {/* 使用PromptItem组件展示 */}
                                <PromptItem
                                    item={item}
                                    expandedState={getCompleteExpandedState(item.decisionId)}
                                    onExpandedStateChange={(key, value) =>
                                        updateItemExpandedState(item.decisionId, key, value)
                                    }
                                    // 在弹窗中不显示parentId点击,避免嵌套弹窗
                                    onParentIdClick={undefined}
                                    publicView={publicView}
                                />
                            </div>
                        ))}
                    </div>
                )
            ) : (
                // 会话消息视图
                sessionLoading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px'
                    }}>
                        <Spin size="large">
                            <div style={{marginTop: 8, color: '#999'}}>加载消息中...</div>
                        </Spin>
                    </div>
                ) : displayMessages.length === 0 ? (
                    <Empty
                        description="暂无会话消息"
                        style={{marginTop: '60px'}}
                    />
                ) : (
                    <div>
                        {displayMessages.map((msg, index) => {
                            const toolGroupCount = getToolGroupCount(displayMessages, index);

                            if (toolGroupCount > 0) {
                                // 工具调用结果组的首条消息，渲染整个组
                                const groupMessages = getToolGroupMessages(displayMessages, index);
                                const isExpanded = expandedToolGroup.has(index);

                                return (
                                    <div key={`tool-group-${index}`} style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        marginBottom: '16px'
                                    }}>
                                        {/* 工具图标 */}
                                        <Tooltip
                                            title={isExpanded ? "点击收起" : `点击展开 ${toolGroupCount} 个工具调用结果`}>
                                            <div
                                                onClick={() => toggleToolGroup(index)}
                                                style={{
                                                    position: 'relative',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    backgroundColor: isExpanded ? 'rgba(250, 140, 22, 0.5)' : 'rgba(250, 140, 22, 0.3)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    flexShrink: 0,
                                                    transition: 'all 0.2s ease',
                                                    border: isExpanded ? '1px solid rgba(250, 140, 22, 0.8)' : '1px solid rgba(250, 140, 22, 0.5)'
                                                }}
                                            >
                                                <ToolOutlined style={{color: 'var(--pt-amber)', fontSize: '16px'}}/>
                                                {!isExpanded && toolGroupCount > 1 && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '-4px',
                                                        right: '-4px',
                                                        backgroundColor: 'var(--pt-amber)',
                                                        color: '#ffffff',
                                                        fontSize: '10px',
                                                        fontWeight: 'bold',
                                                        padding: '0 4px',
                                                        borderRadius: '8px',
                                                        minWidth: '14px',
                                                        height: '14px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '1px solid var(--pt-bg-panel)'
                                                    }}>
                                                        {toolGroupCount}
                                                    </div>
                                                )}
                                            </div>
                                        </Tooltip>

                                        {/* 工具调用结果内容 */}
                                        {isExpanded && (
                                            <div style={{
                                                flex: 1,
                                                backgroundColor: '#3a3a3a',
                                                borderRadius: '8px',
                                                padding: '12px 16px',
                                                border: '1px solid rgba(255,255,255,0.07)',
                                                color: '#ffffff',
                                                minWidth: 0
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    marginBottom: '12px',
                                                    paddingBottom: '8px',
                                                    borderBottom: '1px solid #505050'
                                                }}>
                                                    <span style={{fontSize: '12px', color: 'var(--pt-amber)'}}>
                                                        <ToolOutlined style={{marginRight: 4}}/>
                                                        工具调用结果 {toolGroupCount > 1 && `(${toolGroupCount}个)`}
                                                    </span>
                                                    <button
                                                        onClick={() => toggleToolGroup(index)}
                                                        style={{
                                                            padding: '2px 8px',
                                                            fontSize: '11px',
                                                            backgroundColor: 'transparent',
                                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                                            borderRadius: '4px',
                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                            cursor: 'pointer'
                                                        }}
                                                    >收起
                                                    </button>
                                                </div>
                                                {groupMessages.map((toolMsg, toolIdx) => (
                                                    <div key={toolMsg.messageId} style={{
                                                        marginBottom: toolIdx < groupMessages.length - 1 ? '12px' : '0',
                                                        paddingBottom: toolIdx < groupMessages.length - 1 ? '12px' : '0',
                                                        borderBottom: toolIdx < groupMessages.length - 1 ? '1px solid #505050' : 'none'
                                                    }}>
                                                        <div style={{
                                                            fontSize: '11px',
                                                            color: 'rgba(255, 255, 255, 0.4)',
                                                            marginBottom: '4px'
                                                        }}>
                                                            工具调用 #{toolIdx + 1}
                                                        </div>
                                                        <MarkdownRenderer
                                                            content={toolMsg.content}
                                                            style={{color: '#d4d4d4', fontSize: '13px'}}
                                                            showCopyButton={false}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }

                            if (msg.role === 'tool') return null; // 跳过非首条tool消息

                            return <ChatMessageComponent key={msg.messageId} message={msg}/>;
                        })}
                    </div>
                )
            )}
        </Modal>
    );
};

export default ConversationChainModal;
